'use client';

import { Box } from '@terra/ui-styled-system';
import { MetaDefault } from '../../../components/typography';
import { useChatExpansionState } from '../expansion/chat-expansion-state';
import type { OnOpenChatObject } from './hitl-types';
import { ToolResultRenderer } from './ToolCallsRenderers/ToolResultRenderer';
import { prepareToolResultData } from './ToolCallsRenderers/content-detection';
import { hasRenderableNode } from './chat-renderability';
import {
  TraceCollapsible,
  TraceCollapsibleContent,
  TraceCollapsibleTrigger,
  TraceLine,
} from './trace';
import type { ChatHeadlineKind } from './chat-headline-actions';
import {
  ChatHeadlineStatusIcon,
  type ChatHeadlineStatusState,
} from './chat-headline-loading';

type InlineToolExpandProps = {
  toolName: string;
  displayText: string;
  children?: React.ReactNode;
  nestedContent?: React.ReactNode;
  defaultExpanded?: boolean;
  status?: string;
  error?: string;
  isSubAgent?: boolean;
  autoCollapseWhenComplete?: boolean;
  forceExpanded?: boolean;
  onOpenObject?: OnOpenChatObject;
  summary?: string;
  liveLabel?: string;
  expansionKey?: string;
  trailingAction?: React.ReactNode;
  rowId?: string;
  headlineKind?: ChatHeadlineKind;
};

const looksLikeMarkdown = (content: string): boolean => {
  return (
    content.includes('##') ||
    content.includes('**') ||
    /^[-*]\s/m.test(content) ||
    /^\d+\.\s/m.test(content) ||
    content.includes('```')
  );
};

const isShortErrorMessage = (error: string): boolean => {
  return error.length < 500 && !looksLikeMarkdown(error);
};

function resolveHeadlineStatusState({
  headlineKind,
  isStarted,
  status,
  isRealError,
}: {
  headlineKind?: ChatHeadlineKind;
  isStarted: boolean;
  status?: string;
  isRealError: boolean;
}): ChatHeadlineStatusState | undefined {
  if (!headlineKind) {
    return undefined;
  }

  if (isStarted) {
    return 'running';
  }

  if (status === 'aborted') {
    return 'stopped';
  }

  if (isRealError || status === 'error' || status === 'failed') {
    return 'failed';
  }

  if (status === 'completed') {
    return 'completed';
  }

  return undefined;
}

export function InlineToolExpand({
  toolName,
  displayText,
  children,
  nestedContent,
  defaultExpanded = false,
  status,
  error,
  isSubAgent = false,
  forceExpanded,
  onOpenObject,
  summary,
  liveLabel,
  expansionKey,
  trailingAction,
  rowId,
  headlineKind,
}: InlineToolExpandProps) {
  const isStarted = status === 'started' || status === 'in-progress';
  const isRealError =
    status === 'error' && (!error || isShortErrorMessage(error));
  const hasErrorContent = !!error;
  const [isExpanded, setIsExpanded] = useChatExpansionState({
    expansionKey,
    defaultExpanded: defaultExpanded || isRealError || forceExpanded === true,
    forceExpanded,
  });
  const effectiveIsExpanded = isExpanded;
  const hasRenderableChildren = hasRenderableNode(children);
  const hasRenderableNested = hasRenderableNode(nestedContent);
  const canExpand =
    hasRenderableChildren || hasRenderableNested || hasErrorContent;
  const compactLiveLabel =
    isStarted && liveLabel && liveLabel !== displayText ? liveLabel : undefined;
  const resolvedSummary = compactLiveLabel ?? summary;
  const headlineStatusState = resolveHeadlineStatusState({
    headlineKind,
    isStarted,
    status,
    isRealError,
  });
  const leading =
    headlineKind && headlineStatusState ? (
      <ChatHeadlineStatusIcon kind={headlineKind} state={headlineStatusState} />
    ) : undefined;

  const renderErrorContent = () => {
    if (!error) return null;

    if (looksLikeMarkdown(error)) {
      return (
        <Box>
          <ToolResultRenderer
            data={prepareToolResultData({
              content: error,
              toolName,
            })}
            onOpenObject={onOpenObject}
          />
        </Box>
      );
    }

    return (
      <Box p={2}>
        <MetaDefault color="fg.muted">{error}</MetaDefault>
      </Box>
    );
  };

  const showContent =
    effectiveIsExpanded &&
    (hasRenderableChildren || hasRenderableNested || hasErrorContent);

  return (
    <TraceCollapsible
      open={effectiveIsExpanded}
      onOpenChange={({ open }) => {
        if (canExpand) {
          setIsExpanded(open);
        }
      }}
      disabled={!canExpand}
    >
      <TraceCollapsibleTrigger disabled={!canExpand}>
        <TraceLine
          id={rowId}
          title={displayText}
          description={effectiveIsExpanded ? undefined : resolvedSummary}
          canExpand={canExpand}
          isExpanded={effectiveIsExpanded}
          status={isStarted ? 'running' : undefined}
          icon={leading}
          trailing={
            trailingAction ? (
              <Box flexShrink={0} ml="auto">
                {trailingAction}
              </Box>
            ) : null
          }
        />
      </TraceCollapsibleTrigger>
      <TraceCollapsibleContent open={showContent}>
        <Box pt={2} mt={1}>
          {hasRenderableNested && nestedContent}
          {(hasRenderableChildren || hasErrorContent) && (
            <Box w="full" pr={2}>
              {hasErrorContent && renderErrorContent()}
              {children}
            </Box>
          )}
        </Box>
      </TraceCollapsibleContent>
    </TraceCollapsible>
  );
}
